import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectAvailableLanguages } from "../../app/slices/TrainingLocalizationSlice";
import { TrainingType } from "../../app/slices/TrainingSlice";
import PiDropdown from "../../components/PiDropdown";
import PiInputfield from "../../components/PiInputField";
import { TranslatedEnvironmentDTO } from "../../dto/TranslatedEnvironmentDTO";

interface DetailsStepProps {
  type: TrainingType;
  environment?: TranslatedEnvironmentDTO;
  name: string;
  onNameChange: (name: string) => void;
  language: string;
  onLanguageChange: (shortcode: string) => void;
  onChangeEnvironment: () => void;
}

/** Last wizard step. 3D confirms the environment in a band above the form; 360 is name + language only. */
export default function DetailsStep(props: DetailsStepProps) {
  const {
    type,
    environment,
    name,
    onNameChange,
    language,
    onLanguageChange,
    onChangeEnvironment,
  } = { ...props };
  const { t } = useTranslation();
  const languages = useSelector(selectAvailableLanguages);
  const is3D = type === TrainingType.Training3D;

  const nameField = (
    <PiInputfield
      label={t("trainingName")}
      placeholder={t("trainingName")}
      value={name}
      onChange={onNameChange}
      autoFocus
      id="new-training-name"
      debounceTime={0}
    />
  );

  const languageField = (
    <PiDropdown
      selectedText={
        languages.find((lang) => lang.shortcode === language)?.full ?? ""
      }
      options={languages.map((lang) => ({
        text: lang.full,
        onClick: () => onLanguageChange(lang.shortcode),
      }))}
      width="100%"
      label={t("training_reference_language") ?? ""}
      info={t("training_reference_language_info") ?? ""}
    />
  );

  // Placeholder until categories exist; matches what the old 3D form showed.
  const categoryField = (
    <PiDropdown
      selectedText={"General Safety"}
      options={[]}
      width="100%"
      label={t("category") ?? ""}
    />
  );

  return (
    <div
      className="flex flex-col gap-y-[24px]"
      data-testid="new-training-step-details"
    >
      {is3D && environment && (
        <div
          className="flex items-center gap-[16px] p-[10px] rounded-[8px] border border-solid border-creator-border"
          data-testid="new-training-env-band"
        >
          {environment.thumbnailUri ? (
            <img
              src={environment.thumbnailUri}
              alt=""
              className="w-[128px] h-[72px] rounded-[4px] object-cover shrink-0"
            />
          ) : (
            <div className="w-[128px] h-[72px] rounded-[4px] shrink-0 bg-interaction-softBackground" />
          )}
          <div className="flex flex-col gap-y-[2px] min-w-0 flex-1">
            <span className="font-semibold text-[16px] text-creator-text truncate">
              {environment.name}
            </span>
            <span className="text-[14px] text-creator-text-sub truncate">
              {environment.description}
            </span>
          </div>
          <button
            type="button"
            onClick={onChangeEnvironment}
            className="shrink-0 px-[8px] py-[4px] text-[14px] font-medium text-creator-primary hover:text-creator-primary-hover bg-transparent border-none cursor-pointer"
            data-testid="new-training-env-change"
          >
            {t("new_training_wizard.change")}
          </button>
        </div>
      )}

      {is3D ? (
        <div className="grid grid-cols-2 gap-x-[40px] gap-y-[20px]">
          <div>{nameField}</div>
          <div>{categoryField}</div>
          <div>{languageField}</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-[40px]">
          <div>{nameField}</div>
          <div>{languageField}</div>
        </div>
      )}
    </div>
  );
}
